/*
 * LICENSE PROTECTED SOFTWARE
 *
 * Bu yazilim lisans korumasi altindadir.
 * Gecerli lisans olmadan kullanilmasi yasaktir.
 *
 * Kaynak kodunun izinsiz erisimi, kopyalanmasi veya dagitimi
 * hukuki yaptirimslara tabidir.
 * (c) 2026  @cemdemirci0101
 *  bilgi@cemdemirci.com
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution
 * of this file, via any medium, is strictly prohibited.
 * (c) 2026 All Rights Reserved.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true,
      });
      setUser(response.data);
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(
      `${API_URL}/api/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    setUser(response.data);
    return response.data;
  };

  const register = async (userData) => {
    const response = await axios.post(
      `${API_URL}/api/auth/register`,
      userData,
      { withCredentials: true }
    );
    if (response.data.requires_verification) return response.data;
    setUser(response.data);
    return response.data;
  };

  const registerMuhtar = async (userData) => {
    const response = await axios.post(
      `${API_URL}/api/auth/register/muhtar`,
      userData,
      { withCredentials: true }
    );
    if (response.data.requires_verification) return response.data;
    setUser(response.data);
    return response.data;
  };

  const registerSTK = async (userData) => {
    const response = await axios.post(
      `${API_URL}/api/auth/register/stk`,
      userData,
      { withCredentials: true }
    );
    if (response.data.requires_verification) return response.data;
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    const csrfMatch = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]*)/);
    const csrfToken = csrfMatch ? decodeURIComponent(csrfMatch[1]) : '';
    await axios.post(`${API_URL}/api/auth/logout`, {}, {
      withCredentials: true,
      headers: { 'X-CSRF-Token': csrfToken },
    });
    setUser(false);
  };

  const refreshToken = async () => {
    try {
      const csrfMatch = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]*)/);
      const csrfToken = csrfMatch ? decodeURIComponent(csrfMatch[1]) : '';
      await axios.post(`${API_URL}/api/auth/refresh`, {}, {
        withCredentials: true,
        headers: { 'X-CSRF-Token': csrfToken },
      });
      await checkAuth();
    } catch (error) {
      setUser(false);
    }
  };

  const hasPermission = useCallback((permissionCode) => {
    if (!user || !user.permissions) return false;
    return user.permissions.includes(permissionCode);
  }, [user]);

  const hasAnyPermission = useCallback((codes) => {
    if (!user || !user.permissions) return false;
    return codes.some(c => user.permissions.includes(c));
  }, [user]);

  const value = {
    user,
    loading,
    login,
    register,
    registerMuhtar,
    registerSTK,
    logout,
    refreshToken,
    checkAuth,
    hasPermission,
    hasAnyPermission,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isGenelSekreter: user?.role === 'genel_sekreter',
    isMuhtar: user?.role === 'muhtar',
    isSTK: user?.role === 'stk',
    isVatandas: user?.role === 'vatandas',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
